import React from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import './style.scss';

/*
 * Shared tooltip host. Nearly every app mounts one of these with the same id
 * ("tuva-tooltip-id") so any component can point at it via data-tooltip-id.
 *
 * Django pages routinely run SEVERAL React roots at once — an app plus
 * common-modals, header, and so on. Each root mounting its own host meant a
 * hovered anchor was answered by all of them, drawing two identical tooltips
 * slightly offset from each other.
 *
 * So an id is claimed by the first host to mount it; later hosts render nothing.
 * The registry lives on `window`, not in module scope: each app is a separate
 * bundle with its own copy of this module, so module scope isn't shared between
 * the roots that need to agree.
 *
 * react-tooltip v5 doesn't put the id on its DOM node, so a querySelector check
 * isn't an option.
 */
const REGISTRY = '__tuvaToolTipHostIds';

function claim(id) {
  if (typeof window === 'undefined' || !id) return true;
  const ids = (window[REGISTRY] = window[REGISTRY] || new Set());
  if (ids.has(id)) return false;
  ids.add(id);
  return true;
}

function release(id) {
  if (typeof window === 'undefined' || !id) return;
  window[REGISTRY]?.delete(id);
}

class ToolTip extends React.Component {
  constructor(props) {
    super(props);
    // Claimed in the constructor rather than an effect: sibling roots mount in
    // the same tick, and a later claim would let both render before either
    // noticed the other.
    this.owns = claim(props.id);
  }

  componentWillUnmount() {
    if (this.owns) release(this.props.id);
  }

  render() {
    // A duplicate host renders nothing — the owner already serves every anchor
    // carrying this id, wherever in the document it sits.
    if (!this.owns) return null;

    return (
      <ReactTooltip
        id={this.props.id}
        overridePosition={this.props.overridePosition}
        effect="solid"
        multiline={true}
        place={this.props.place}
        className={`tuva-tooltip ${
          this.props.className ? this.props.className : ''
        }`}
        clickable={true}
      />
    );
  }
}

export default ToolTip;
